import { Modal, notification } from 'antd'
import {
  ActivityLogAction,
  postActivityLog,
} from 'modules/activity-log-service'
import { useState } from 'react'
import { removeAssignee } from '../api/application-queue-enrollment-platform-remove-assignee'
import { useBenefitQueuesQuery } from './useBenefitQueuesQuery'

export function useRemoveAssignee() {
  const [isRemoving, setIsRemoving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { queries } = useBenefitQueuesQuery()

  const handleConfirmModal = async (
    applicationPublicKey: string,
    employeeName: string,
    assignee: string,
    onSuccess?: () => void,
    employeePublicKey?: string
  ) => {
    try {
      setIsRemoving(true)
      setError(null)
      await removeAssignee(applicationPublicKey)
      await queries
        .find((query) => query.data?.assignee === assignee)
        ?.refetch()
      await postActivityLog({
        action: ActivityLogAction.REMOVE_ASSIGNEE,
        description: `manual removed assignee ${assignee} from application ${employeeName}`,
        targetUserId: employeePublicKey,
        targetName: employeeName,
        additionalData: {
          applicationId: applicationPublicKey,
          employeeName,
          assignee,
        },
      })
      notification.success({
        message: `${employeeName} removed from the queue successfully`,
      })
      onSuccess?.()
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to remove assignee'
      setError(errorMessage)
      notification.error({
        message: `Failed to remove assignee: ${errorMessage}`,
      })
      console.error('Error removing assignee:', err)
    } finally {
      setIsRemoving(false)
    }
  }

  const removeAssigneeFromQueue = (
    applicationPublicKey: string,
    employeeName: string,
    assignee: string,
    onSuccess?: () => void,
    employeePublicKey?: string
  ) => {
    Modal.confirm({
      title: 'Remove from queue',
      content: `Are you sure you want to remove ${employeeName} from the queue?`,
      okText: 'Remove',
      cancelText: 'Cancel',
      onOk: () =>
        handleConfirmModal(
          applicationPublicKey,
          employeeName,
          assignee,
          onSuccess,
          employeePublicKey
        ),
      onCancel: () => setIsRemoving(false),
    })
  }

  return {
    removeAssigneeFromQueue,
    isRemoving,
    error,
  }
}
